import { cn } from '@onclicks/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        clear: '',
        default: 'h-10 px-4 py-2',
        icon: 'h-10 w-10',
        lg: 'h-11 rounded px-8',
        sm: 'h-9 rounded px-3',
      },
      variant: {
        default: cn(
          'inline-flex items-center justify-center px-8 py-3 font-semibold tracking-widest text-primary uppercase bg-no-repeat bg-[length:calc(100%-14px)_1px,1px_calc(100%-14px),calc(100%-14px)_1px,1px_calc(100%-14px)] bg-[position:top_left,right_bottom,bottom_right,left_top] bg-[image:linear-gradient(to_right,theme(colors.primary),theme(colors.primary)),linear-gradient(to_bottom,theme(colors.primary),theme(colors.primary)),linear-gradient(to_right,theme(colors.primary),theme(colors.primary)),linear-gradient(to_bottom,theme(colors.primary),theme(colors.primary))] hover:bg-primary hover:text-white transition-colors',
        ),
        defaultReversed: cn(
          'inline-flex items-center justify-center px-8 py-3 font-semibold tracking-widest text-primary uppercase bg-[length:calc(100%-14px)_1px,1px_calc(100%-14px),calc(100%-14px)_1px,1px_calc(100%-14px)] bg-[position:top_right,left_bottom,bottom_left,right_top] bg-no-repeat bg-[image:linear-gradient(to_right,theme(colors.primary),theme(colors.primary)),linear-gradient(to_bottom,theme(colors.primary),theme(colors.primary)),linear-gradient(to_right,theme(colors.primary),theme(colors.primary)),linear-gradient(to_bottom,theme(colors.primary),theme(colors.primary))] hover:bg-primary hover:text-white transition-colors',
        ),
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost: 'hover:bg-card hover:text-accent-foreground',
        link: 'text-primary items-start justify-start underline-offset-4 hover:underline',
        outline: cn(
          'border border-t-transparent border-r-transparent border-l-transparent border-b-primary pb-1 font-semibold tracking-widest text-primary uppercase transition-colors hover:border hover:border-primary',
        ),
        secondary: 'text-white p-0',
        basic: cn('text-black text-lg p-0 uppercase'),
        basicWhite: cn(
          'text-white p-0 hover:decoration-primary hover:decoration-2 hover:underline hover:underline-offset-2',
        ),
        inputStyle: cn(
          'bg-white !px-0 !pl-3 !pr-10 text-gray-400 rounded hover:ring-2 hover:ring-primary',
        ),
        gold: cn('text-primary p-0 hover:font-bold'),
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
}

export { Button, buttonVariants }
